import { usePermissions } from "@/utils/permissions"
import React from "react"

interface PermissionGuardProps {
  permission?: string
  permissions?: string[]
  requireAll?: boolean
  fallback?: React.ReactNode
  children: React.ReactNode
}

/**
 * Component wrapper để hiển thị nội dung dựa trên quyền
 * 
 * @example
 * // Kiểm tra một quyền
 * <PermissionGuard permission={PERMISSIONS.CASH_FLOWS_CONFIRM}>
 *   <Button>Xác nhận</Button>
 * </PermissionGuard>
 * 
 * @example
 * // Kiểm tra nhiều quyền (chỉ cần một)
 * <PermissionGuard permissions={[PERMISSIONS.CASH_FLOWS_CREATE, PERMISSIONS.CASH_FLOWS_UPDATE]}>
 *   <Button>Tạo/Sửa</Button>
 * </PermissionGuard>
 * 
 * @example
 * // Kiểm tra nhiều quyền (cần tất cả)
 * <PermissionGuard permissions={[PERMISSIONS.CASH_FLOWS_CREATE, PERMISSIONS.CASH_FLOWS_UPDATE]} requireAll>
 *   <Button>Tạo và Sửa</Button>
 * </PermissionGuard>
 * 
 * @example
 * // Với fallback
 * <PermissionGuard permission={PERMISSIONS.CASH_FLOWS_DELETE} fallback={<span>Không có quyền xóa</span>}>
 *   <Button danger>Xóa</Button>
 * </PermissionGuard>
 */
export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  permission,
  permissions,
  requireAll = false,
  fallback = null,
  children
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions()

  // Kiểm tra quyền
  let hasAccess = false

  if (permission) {
    // Kiểm tra một quyền cụ thể
    hasAccess = hasPermission(permission)
  } else if (permissions && permissions.length > 0) {
    // Kiểm tra nhiều quyền
    if (requireAll) {
      hasAccess = hasAllPermissions(permissions)
    } else {
      hasAccess = hasAnyPermission(permissions)
    }
  } else {
    // Không có quyền nào được chỉ định, luôn hiển thị
    hasAccess = true
  }

  // Trả về children nếu có quyền, ngược lại trả về fallback
  return hasAccess ? <>{children}</> : <>{fallback}</>
}

export default PermissionGuard 